import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { GripVertical, Plus, Copy, Trash2, Pencil } from 'lucide-react';
import RoutineExerciseCard from './RoutineExerciseCard';
import { WorkSetsTags } from '../RoutineEditor';
import { MuscleBodyMapCompact } from '../../MuscleBodyMap';

export default function RoutineBoardColumn({
  day, dayIdx, dayStats = {}, dayCount, language, exerciseMuscleMap, exerciseImages, isCoachOrAdmin,
  onRenameDay, onDuplicateDay, onDeleteDay, onAddExercise, onOpenMuscleDetail, cardCallbacks,
}) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(day.day_name || '');

  const { attributes, listeners, setNodeRef: setColRef, transform, transition, isDragging } = useSortable({
    id: `col-${day.id}`, data: { type: 'column', dayIdx },
  });
  const { setNodeRef: setDropRef } = useDroppable({ id: `daydrop-${dayIdx}`, data: { type: 'column-body', dayIdx } });

  const style = { transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const exercises = day.exercises || [];
  const dayCountSum = Object.values(dayStats).reduce((a, b) => a + b, 0);

  const commitRename = () => {
    const v = renameValue.trim();
    if (v && v !== day.day_name) onRenameDay(dayIdx, v);
    setRenaming(false);
  };

  return (
    <div ref={setColRef} style={style}
      className="flex flex-col w-full lg:w-[300px] lg:flex-shrink-0 rounded-2xl border border-border/50 bg-muted/20 lg:max-h-full"
      data-testid={`board-column-${dayIdx}`}>
      {/* Header */}
      <div className="flex items-center gap-2 p-3 border-b border-border/30">
        <button {...attributes} {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-primary flex-shrink-0 touch-none"
          data-testid={`board-column-drag-${dayIdx}`} aria-label={t('routineEditor.reorder')}>
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="w-7 h-7 rounded-lg bg-primary flex items-center justify-center text-white font-bold text-xs flex-shrink-0">{dayIdx + 1}</div>
        {renaming ? (
          <input autoFocus value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setRenaming(false); }}
            className="flex-1 min-w-0 font-semibold text-sm bg-transparent border-b-2 border-primary outline-none py-0.5"
            data-testid={`board-rename-input-${dayIdx}`} />
        ) : (
          <button onClick={() => { setRenameValue(day.day_name || ''); setRenaming(true); }}
            className="flex-1 min-w-0 text-left font-semibold text-sm truncate" data-testid={`board-day-name-${dayIdx}`}>
            {day.day_name}
          </button>
        )}
        <button onClick={() => { setRenameValue(day.day_name || ''); setRenaming(true); }} className="p-1 text-muted-foreground hover:text-primary flex-shrink-0" title={t('tooltips.edit')} data-testid={`board-rename-${dayIdx}`}>
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => onDuplicateDay(dayIdx)} className="p-1 text-muted-foreground hover:text-primary flex-shrink-0" title={t('tooltips.duplicate')} data-testid={`board-duplicate-${dayIdx}`}>
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => onDeleteDay(dayIdx)} disabled={dayCount <= 1}
          className="p-1 text-muted-foreground hover:text-destructive disabled:opacity-30 flex-shrink-0" title={t('tooltips.removeDay')} data-testid={`board-delete-day-${dayIdx}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Per-day volume */}
      {dayCountSum > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border/20" data-testid={`board-day-volume-${dayIdx}`}>
          <div className="flex-1 min-w-0"><WorkSetsTags stats={dayStats} size="xs" /></div>
          <MuscleBodyMapCompact muscleStats={dayStats} onClick={() => onOpenMuscleDetail?.(dayStats)} />
        </div>
      )}

      {/* Cards (droppable body) */}
      <div ref={setDropRef} className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[60px]" data-testid={`board-column-body-${dayIdx}`}>
        <SortableContext items={exercises.map(e => e.id)} strategy={verticalListSortingStrategy}>
          {exercises.map((ex, exIdx) => (
            <RoutineExerciseCard
              key={ex.id} id={ex.id} exercise={ex} dayIdx={dayIdx} exIdx={exIdx}
              language={language} exerciseMuscleMap={exerciseMuscleMap} exerciseImages={exerciseImages}
              isCoachOrAdmin={isCoachOrAdmin} {...cardCallbacks(dayIdx, exIdx, ex)} />
          ))}
        </SortableContext>
        {exercises.length === 0 && (
          <p className="text-center text-[11px] text-muted-foreground py-4">{t('routineEditor.addFirstExercise')}</p>
        )}
      </div>

      {/* Add exercise */}
      <button onClick={() => onAddExercise(dayIdx)}
        className="m-2 h-9 rounded-lg border border-dashed border-border/60 text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors flex items-center justify-center gap-1"
        data-testid={`board-add-exercise-${dayIdx}`}>
        <Plus className="h-3.5 w-3.5" /> {t('routineEditor.addExercise')}
      </button>
    </div>
  );
}
